import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useCreateMutation } from "../../api/genericHooks";
import { LanguageSelectorModalButton } from "../../components/Buttons/LanguageSelectorModalButton";
import { logo } from "../../images";

type ResetPasswordRequestData = {
  email: string;
};

export const ResetPasswordRequest = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Query Methods
  const resetPasswordQuery = useCreateMutation("/password");

  // Form Methods
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordRequestData>();

  const onSubmit: SubmitHandler<ResetPasswordRequestData> = (data) => {
    resetPasswordQuery
      .mutateAsync({
        user: {
          email: data.email,
        },
      })
      .then(() => {
        toast.success(t("resetPassword.requestSent"));
        navigate("/");
      });
  };

  return (
    <>
      <div className="flex flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-delicate-50">
        <div className="w-full justify-items-end bg-delicate-50 sm:block">
          <LanguageSelectorModalButton />
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            alt="Memoirs Logo"
            src={logo.logoGrey}
            className="mx-auto h-20 w-auto"
          />
          <h2 className="mt-6 font-poppins text-center text-2xl/9 font-bold tracking-tight text-slate-100">
            {t("resetPassword.resetPassword")}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label
                  htmlFor="email"
                  className="block font-nunito text-sm/6 font-medium text-slate-900"
                >
                  {t("resetPassword.email")}
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className="block w-full font-nunito rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                    {...register("email", { required: true })}
                  />
                  {errors.email && (
                    <span className="text-rust-500 font-nunito">
                      {t("errors.required")}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className="flex w-full font-nunito justify-center rounded bg-rust-400 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-rust-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust-400"
                >
                  {t("resetPassword.resetPassword")}
                </button>
              </div>
            </form>
          </div>

          <p className="mt-10 text-center text-sm/6">
            <NavLink
              to="/login"
              className="font-semibold font-nunito text-rust-400 hover:text-rust-50"
            >
              {t("resetPassword.backToLogin")}
            </NavLink>
          </p>
        </div>
      </div>
    </>
  );
};
